// The world's ongoing sound, read off the render state each frame: every cowboy's jetpack
// (same throttle the plumes use), the robots' telegraphs, attacks, alerts, footsteps and
// engine loops, the low-health heartbeat, and which state the score should be in. One call
// from game.ts; nothing here touches rules or the network.
//
// Cost: a few map lookups per robot per frame, and loops only for the nearest few emitters
// (BOMBER_LOOPS, AURA_LOOPS, JETS) so a crowded island never piles up voices.

import type { CowboyRS, EnemyRS } from '../render/contracts';
import type { MusicState, Sfx } from './sfx';

const enum ET { Swarmer = 0, Shooter = 1, Brute = 2, Sniper = 3, Bomber = 4, MetalBrute = 5, Disruptor = 6, Engineer = 7 }
const enum ES { Chase = 0, Windup = 1, Attack = 2, Recover = 3, Stun = 4, Spawn = 5 }

/** telegraph when a robot starts winding up (the "get out of the way" cue), by type */
const TELL: Record<number, [string, number]> = {
  [ET.Swarmer]: ['swarmer_tell', 0.8],
  [ET.Shooter]: ['shooter_tell', 0.7],
  [ET.Brute]: ['brute_tell', 1],
  [ET.MetalBrute]: ['metal_tell', 1],
  [ET.Disruptor]: ['disruptor_sparkle', 1],
  [ET.Engineer]: ['engineer_work', 1],
};
/** the swing / launch itself, for robots whose attack isn't a bullet event */
const STRIKE: Record<number, [string, number]> = {
  [ET.Swarmer]: ['swarmer_slash', 0.9],
  [ET.Brute]: ['brute_slam', 1],
  [ET.MetalBrute]: ['metal_fire', 1],
  [ET.Disruptor]: ['disruptor_throw', 1],
  [ET.Engineer]: ['engineer_gas', 0.9],
};
const BOMBER_LOOPS = 3, AURA_LOOPS = 2, JETS = 4;

interface Bot { st: number; walk: number; x: number; z: number; seen: number; bk: string; ak: string }
interface Near { id: number; d: number; x: number; y: number; z: number; kind: number; key: string }
interface Jet { c: CowboyRS | null; d: number }
interface P3 { x: number; y: number; z: number }

/** a position for a one-shot; sfx.play reads it immediately, so one scratch object is enough */
const at = (p: P3, x: number, y: number, z: number) => { p.x = x; p.y = y; p.z = z; return p; };

export class WorldAudio {
  private bots = new Map<number, Bot>();
  private alertAt = -9;
  private frame = 0;
  // reused every frame: no per-frame garbage on a crowded island
  private loopsOn = new Set<string>();
  private want = new Set<string>();
  private liveJets = new Set<string>();
  private near: Near[] = [];
  private nNear = 0;
  private jets: Jet[] = [];
  private jetKey = new Map<number, string>();
  private p: P3 = { x: 0, y: 0, z: 0 };
  private q: P3 = { x: 0, y: 0, z: 0 };
  private byD = (a: { d: number }, b: { d: number }) => a.d - b.d;

  constructor(private sfx: Sfx) {}

  update(
    dt: number, time: number,
    enemies: readonly EnemyRS[], nEnemies: number,
    cowboys: readonly CowboyRS[], nCowboys: number,
    jetOf: ((id: number) => number) | undefined,
    lx: number, ly: number, lz: number,
    music: MusicState, intensity: number,
    lowHp: number,
  ) {
    const sfx = this.sfx, p = this.p;
    this.frame++;
    sfx.setMusic(music);
    sfx.setMusicIntensity(intensity);

    // ---- robots: state edges -> telegraph / strike / alert; heavy footsteps
    this.nNear = 0;
    for (let i = 0; i < nEnemies; i++) {
      const e = enemies[i];
      let b = this.bots.get(e.id);
      if (!b) { b = { st: e.st, walk: 0, x: e.x, z: e.z, seen: this.frame, bk: 'bmb' + e.id, ak: 'aur' + e.id }; this.bots.set(e.id, b); }
      b.seen = this.frame;
      if (e.st !== b.st) {
        if (e.st === ES.Windup) { const t = TELL[e.type]; if (t) sfx.play(t[0], at(p, e.x, e.y + 1.2, e.z), { volume: t[1] }); }
        else if (e.st === ES.Attack) { const s = STRIKE[e.type]; if (s) sfx.play(s[0], at(p, e.x, e.y + 1.2, e.z), { volume: s[1] }); }
        else if (b.st === ES.Spawn && e.st === ES.Chase && time - this.alertAt > 0.45) {
          this.alertAt = time;
          sfx.play(e.type === ET.Swarmer ? 'swarmer_alert' : 'robot_alert', at(p, e.x, e.y + 1.2, e.z), { pitch: e.type === ET.Brute || e.type === ET.MetalBrute ? 0.8 : 1 });
        }
        if (e.st === ES.Stun && b.st !== ES.Stun) sfx.play('robot_stagger', at(p, e.x, e.y + 1.2, e.z), { volume: 0.6 });
        b.st = e.st;
      }
      if (e.type === ET.Brute || e.type === ET.MetalBrute) {
        b.walk += Math.hypot(e.x - b.x, e.z - b.z);
        if (b.walk > (e.type === ET.MetalBrute ? 2.6 : 2.1)) { b.walk = 0; sfx.play('brute_step', at(p, e.x, e.y, e.z), { pitch: e.type === ET.MetalBrute ? 0.8 : 1 }); }
      } else if (e.type === ET.Swarmer && e.st === ES.Chase) {
        // swarmers skitter: an occasional hop chirp
        b.walk += Math.hypot(e.x - b.x, e.z - b.z);
        if (b.walk > 9 + (e.id % 5)) { b.walk = 0; sfx.play('swarmer_jump', at(p, e.x, e.y + 1.2, e.z), { volume: 0.5 }); }
      }
      b.x = e.x; b.z = e.z;
      if (e.type === ET.Bomber || e.type === ET.Disruptor) {
        let n = this.near[this.nNear];
        if (!n) { n = { id: 0, d: 0, x: 0, y: 0, z: 0, kind: 0, key: '' }; this.near.push(n); }
        this.nNear++;
        n.id = e.id; n.d = (e.x - lx) ** 2 + (e.y - ly) ** 2 + (e.z - lz) ** 2;
        n.x = e.x; n.y = e.y + 1.5; n.z = e.z;
        n.kind = e.type === ET.Bomber ? 0 : 1; n.key = n.kind === 0 ? b.bk : b.ak;
      }
    }
    if ((this.frame & 63) === 0) for (const [id, b] of this.bots) if (this.frame - b.seen > 30) this.bots.delete(id);

    // engine loops for the nearest bombers / disruptor auras only
    const near = this.near;
    for (let i = this.nNear; i < near.length; i++) near[i].d = Infinity; // stale tail sorts last
    near.sort(this.byD);
    const want = this.want; want.clear();
    let nb = 0, na = 0;
    for (let i = 0; i < this.nNear; i++) {
      const n = near[i];
      if (n.kind === 0 && nb < BOMBER_LOOPS) { nb++; want.add(n.key); sfx.loop(n.key, 'bomber_loop', 1, n, 1 + (n.id % 7) * 0.02); }
      else if (n.kind === 1 && na < AURA_LOOPS) { na++; want.add(n.key); sfx.loop(n.key, 'aura_loop', 1, n); }
    }

    // ---- jetpacks: the nearest few cowboys, the local one always
    const jets = this.jets;
    for (let i = 0; i < nCowboys; i++) {
      const c = cowboys[i];
      let j = jets[i];
      if (!j) { j = { c: null, d: 0 }; jets.push(j); }
      j.c = c; j.d = c.isLocal ? -1 : (c.x - lx) ** 2 + (c.y - ly) ** 2 + (c.z - lz) ** 2;
    }
    for (let i = nCowboys; i < jets.length; i++) { jets[i].c = null; jets[i].d = Infinity; }
    jets.sort(this.byD);
    const liveJets = this.liveJets; liveJets.clear();
    for (let i = 0; i < Math.min(JETS, nCowboys); i++) {
      const c = jets[i].c!;
      const thr = jetOf ? jetOf(c.id) : c.move === 3 ? 0.8 : c.move === 2 ? 0.45 : 0;
      let k = this.jetKey.get(c.id);
      if (!k) { k = 'jet' + c.id; this.jetKey.set(c.id, k); }
      liveJets.add(k);
      sfx.jet(k, c.hidden && !c.isLocal ? 0 : thr, at(this.q, c.x, c.y + 1.1, c.z), c.isLocal);
      if (c.isLocal) {
        const spd = Math.hypot(c.vx, c.vy, c.vz);
        sfx.loop('wind', 'flight_wind', c.move === 3 ? Math.min(1, spd / 30) : c.grounded ? 0 : Math.min(0.5, Math.max(0, spd - 12) / 30));
      }
    }
    for (const k of sfx.jetKeys()) if (!liveJets.has(k)) sfx.jetOff(k);

    // ---- your heartbeat when you're nearly done for
    sfx.loop('lowhp', 'low_hp_loop', lowHp);

    for (const k of this.loopsOn) if (!want.has(k)) sfx.loop(k, 'bomber_loop', 0);
    this.want = this.loopsOn; this.loopsOn = want;
    void dt;
  }
}
